import React, { useState } from 'react';
import { Container, Divider, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Text, Typography } from '@fiverr-private/typography';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleGroup,
  CollapsibleHeader,
  CollapsibleHeaderChevron,
} from '@fiverr-private/disclosure';
import { AppI18n } from '../../../../i18n';
import { EditComponentProps } from '../..';
import { EditActions } from '../../EditActions';
import {
  getCountriesAndRegionsText,
  getFreelancerTypeKeys,
  getFreelancerTypeText,
  getLanguageName,
  getTimezoneText,
} from '../utils/utils';
import { IdealCandidateContextProvider, useIdealCandidateContext } from '../utils/context';
import { useBriefCreationContext } from '../../../../context/BriefCreationContext';
import { Languages, LocationAndTimezone, SkillsAndExperience, TypeAndLevel } from './Sections';
import { TooltipInfo } from './Tooltip';

interface SectionProps {
  title: string;
  children: React.ReactNode;
  openTitle?: string;
  tooltipCopy?: string;
  selectedData?: string[];
  isDefaultOpen?: boolean;
}

const Section = React.memo(
  ({ title, children, openTitle, tooltipCopy, selectedData, isDefaultOpen = false }: SectionProps) => {
    const [isOpen, setIsOpen] = useState(isDefaultOpen);

    return (
      <Collapsible expanded={isOpen} onToggle={setIsOpen}>
        <CollapsibleHeader paddingY="5">
          <Stack justifyContent="spaceBetween">
            {isOpen && openTitle ? (
              <Stack direction="row" alignItems="center" gap="2">
                <Text fontWeight="semibold">
                  <I18n k={`dm_brief_creation.sections.ideal_candidate.${openTitle}`} />
                </Text>
                {tooltipCopy && <TooltipInfo contentCopy={tooltipCopy} />}
              </Stack>
            ) : (
              <Text fontWeight="semibold">
                <I18n k={`dm_brief_creation.sections.ideal_candidate.${title}.title`} />
              </Text>
            )}
            {selectedData && !isOpen && (
              <Typography
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth="180px"
                size="b_sm"
                color="grey_900"
                fontWeight="normal"
              >
                {selectedData.join(', ')}
              </Typography>
            )}
          </Stack>

          <CollapsibleHeaderChevron />
        </CollapsibleHeader>
        <CollapsibleContent>
          <Container paddingBottom="6">{children}</Container>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

Section.displayName = 'Section';

export const EditIdealCandidate = () => {
  const {
    skillsAndExperience,
    languages,
    countriesAndRegions,
    timezone,
    freelancerType,
    chosenCategory,
    hasChanges,
    onEditEnd,
    onCancel,
  } = useIdealCandidateContext();

  const { enableEnhancedUx } = useBriefCreationContext();

  const timezoneSelectedData = timezone?.timezoneId
    ? [getTimezoneText(timezone.timezoneId, timezone.maxOffsetHours)]
    : [];

  return (
    <Stack direction="column">
      {!enableEnhancedUx && (
        <Typography size="b_sm" color="grey_900" fontWeight="normal">
          <AppI18n k={'sections.ideal_candidate.description'} />
        </Typography>
      )}

      <CollapsibleGroup>
        <Section
          title="skills_and_experience"
          selectedData={[skillsAndExperience]}
          isDefaultOpen={!!skillsAndExperience}
        >
          <SkillsAndExperience />
        </Section>
        <Divider />
        <Section
          title="languages"
          selectedData={languages
            .map((language) => getLanguageName(language.languageCode))
            .filter((language): language is string => Boolean(language))}
        >
          <Languages />
        </Section>
        <Divider />
        <Section
          title="location_timezone"
          selectedData={
            chosenCategory === 'location' ? getCountriesAndRegionsText(countriesAndRegions) : timezoneSelectedData
          }
        >
          <LocationAndTimezone />
        </Section>
        <Divider />
        <Section
          title="type_level"
          openTitle="type_level.level.fiverr_level"
          tooltipCopy="type_level.tooltip.fiverr_level"
          selectedData={Object.values(getFreelancerTypeKeys(freelancerType))
            .filter((value) => !!value)
            .map((value) => getFreelancerTypeText((value as string).toLocaleLowerCase()))}
        >
          <TypeAndLevel />
        </Section>
      </CollapsibleGroup>

      <EditActions onCancel={onCancel} onEditEnd={onEditEnd} isSaveDisabled={!hasChanges} />
    </Stack>
  );
};

export const IdealCandidateEdit = ({
  section,
  onChange,
  onEditEnd,
  onCancel,
  shouldRemoveSection,
}: EditComponentProps) => (
  <IdealCandidateContextProvider
    onChange={onChange}
    onEditEnd={onEditEnd}
    onCancel={onCancel}
    content={(section.content || {}) as any}
    isLastIncludedSection={shouldRemoveSection}
  >
    <EditIdealCandidate />
  </IdealCandidateContextProvider>
);
